import { computed } from 'vue'
import { freezeObject } from '@/global/utils/utils'
import WorkingHoursSection from '../components/WorkingHoursSection.vue'
import CrewMembersSection from '../components/CrewMembersSection.vue'
import { crewDataFetched, crewModifyDataBeforeSend } from './crewApiTransforms'

const TAB_KEY = 'basic_info'

export function useCrewDetails (options) {
  const {
    translate,
    tabActionsComponent,
    resolveRouteParamValue,
    columnsContentComponent
  } = options || {}

  const apiConfig = freezeObject({
    post: {
      module: 'road-maintenance',
      route: `crews/${resolveRouteParamValue('id')}`
    },
    get: {
      module: 'road-maintenance',
      route: `crews/${resolveRouteParamValue('id')}`
    }
  })

  const tabActions = computed(() => ({
    component: tabActionsComponent,
    title: translate('road-maintenance/temporary_regulation/crew_details.crew_details'),
    showEditButtons: true,
    showBackButton: {
      uiViewName: 'TemporaryRegulationsCrews'
    },
    visible: true
  }))

  const basicInfoFieldsBlock = computed(() => ({
    type: 'fields',
    columnsCount: 4,
    fields: [
      {
        key: 'name',
        label: translate('road-maintenance/temporary_regulation/crew_details.crew_name'),
        creatable: true,
        editable: true,
        visible: true
      },
      {
        key: 'status_id',
        // crewModifyDataBeforeSend renames this to `status_name` before it's
        // sent to the backend, so that's the key its validation errors come
        // back under, not `status_id`.
        validationPropertyKey: 'status_name',
        label: translate('road-maintenance/temporary_regulation/crews.status'),
        type: 'autocomplete',
        creatable: true,
        editable: true,
        visible: true,
        items: [
          {
            id: 'ACTIVE',
            name: translate('base.active')
          },
          {
            id: 'INACTIVE',
            name: translate('base.inactive')
          }
        ]
      },
      {
        key: 'base_geolocation_id',
        label: translate('locations.location'),
        type: 'autocomplete',
        creatable: true,
        editable: true,
        visible: true,
        autocomplete_options: {
          module: 'road-maintenance',
          route: 'work-order-geolocations/autocomplete',
          custom_display_value: (item) => item?.description || ''
        }
      }
    ]
  }))

  const basicInfoBlock = computed(() => ({
    component: columnsContentComponent,
    title: translate('fleet/vehicle.basic_info'),
    visible: true,
    showButtons: false,
    type: 'columns',
    rows: [
      basicInfoFieldsBlock.value
    ]
  }))

  const workingHoursBlock = computed(() => ({
    component: WorkingHoursSection,
    title: translate('road-maintenance/temporary_regulation/crew_details.working_hours'),
    type: 'dynamic-columns',
    key: 'working_hours',
    dataKey: 'working_hours'
  }))

  const responsiblePersonFieldsBlock = computed(() => ({
    type: 'fields',
    columnsCount: 4,
    fields: [
      {
        key: 'responsible_person_id',
        label: translate('road-maintenance/temporary_regulation/crews.responsible_person'),
        type: 'autocomplete',
        creatable: true,
        editable: true,
        visible: true,
        autocomplete_options: {
          module: 'people',
          route: 'people/autocomplete'
        }
      },
      {
        key: 'vehicle_id',
        label: translate('fleet.vehicle'),
        type: 'autocomplete',
        creatable: true,
        editable: true,
        visible: true,
        autocomplete_options: {
          module: 'fleet',
          route: 'vehicles/autocomplete'
        }
      }
    ]
  }))

  const responsiblePersonBlock = computed(() => ({
    component: columnsContentComponent,
    title: translate('road-maintenance/temporary_regulation/crews.responsible_person'),
    visible: true,
    showButtons: false,
    type: 'columns',
    rows: [
      responsiblePersonFieldsBlock.value
    ]
  }))

  const crewMembersBlock = computed(() => ({
    component: CrewMembersSection,
    title: translate('road-maintenance/temporary_regulation/crews.crew'),
    type: 'dynamic-columns',
    key: 'crew_members',
    dataKey: 'crew_members',
    autocomplete_options: {
      module: 'people',
      route: 'people/autocomplete'
    }
  }))

  return computed(() => ({
    title: translate('road-maintenance/temporary_regulation/crews.crew_details'),
    key: TAB_KEY,
    apiConfig,
    dataFetched: crewDataFetched,
    modifyDataBeforeSend: crewModifyDataBeforeSend,
    afterSave: (contentInstance, data) => crewDataFetched({
      data: { data },
      updateData: (val) => contentInstance.updateData(val)
    }),
    afterCancel: (contentInstance) => crewDataFetched({
      data: { data: contentInstance.originalFormData },
      updateData: (val) => contentInstance.updateData(val)
    }),
    tabContent: [
      tabActions.value,
      basicInfoBlock.value,
      workingHoursBlock.value,
      responsiblePersonBlock.value,
      crewMembersBlock.value
    ]
  }))
}
